/* ============================================================
   cp-interactions.js — CathyPaul Diamond Homepage Motion Layer
   Behaviors: (B) Scroll Reveal, (D) Stat Count-Up,
              (F1) Magnetic CTAs, (F2) Card Spotlight
   Idempotent: guards against double-init.
   All motion degrades under prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  /* ---- idempotent guard ---- */
  if (window.__cpInteractionsInit) return;
  window.__cpInteractionsInit = true;

  /* ---- capability flags ---- */
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine   = window.matchMedia('(pointer:fine)').matches;

  /* ===========================================================
     (B) SCROLL REVEAL
     =========================================================== */
  function initScrollReveal() {
    /* If user prefers reduced motion: do nothing — CSS forces
       .cp-reveal elements visible via the override block.       */
    if (reduce) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

    /* Standalone elements */
    var standaloneSelectors = [
      '.cp-cats__heading',
      '.cp-sf__heading',
      '.cp-sf__lead',
      '.cp-sf__stats',
      '.cp-sf__features',
      '.cp-sf__media-col',
      '.cp-pf__heading',
      '.cp-pf__lead',
      '.cp-pf__stats',
      '.cp-pf__features',
      '.cp-pf__media-col'
    ];

    standaloneSelectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.classList.add('cp-reveal');
        io.observe(el);
      });
    });

    /* Staggered group helper */
    function staggerChildren(containerSel, childSel) {
      document.querySelectorAll(containerSel).forEach(function (container) {
        var children = container.querySelectorAll(childSel);
        var count = 0;
        children.forEach(function (child) {
          child.classList.add('cp-reveal');
          var delay = Math.min(count, 6) * 70;
          child.style.setProperty('--cp-reveal-delay', delay + 'ms');
          io.observe(child);
          count++;
        });
      });
    }

    /* Featured categories cards */
    staggerChildren('.cp-cats__grid', '.cp-cats__card');

    /* Product scroller cards */
    staggerChildren('.cp-pscroll__track', '.cp-pscroll__card');

    /* Explore fancy colour tiles */
    staggerChildren('.cp-ef__grid', '.cp-ef__card');

    /* Customer moments tiles */
    staggerChildren('.cp-cm__wall', '.cp-cm__moment');
  }

  /* ===========================================================
     (D) STAT COUNT-UP
     =========================================================== */
  function initStatCountUp() {
    if (reduce) return;

    /* Regex: bare number optionally ending with % — e.g. "6", "0.01%", "100"
       Rejects anything with letters, CJK, dash, slash.           */
    var numRe = /^(\d+(\.\d+)?)(%?)$/;

    var countIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        countIo.unobserve(entry.target);
        animateStat(entry.target);
      });
    }, { threshold: 0.4 });

    document.querySelectorAll('.cp-sf__stat-value, .cp-pf__stat-value').forEach(function (el) {
      var raw = (el.textContent || '').trim();
      if (!numRe.test(raw)) return; /* static — letters/CJK/dashes/etc. */

      var match    = numRe.exec(raw);
      var target   = parseFloat(match[1]);
      var suffix   = match[3] || '';
      var decimals = match[2] ? (match[2].length - 1) : 0; /* chars after dot */

      /* Store originals on element for restoration */
      el.dataset.cpStatTarget   = String(target);
      el.dataset.cpStatSuffix   = suffix;
      el.dataset.cpStatDecimals = String(decimals);
      el.dataset.cpStatOriginal = raw;

      countIo.observe(el);
    });
  }

  function animateStat(el) {
    var target   = parseFloat(el.dataset.cpStatTarget);
    var suffix   = el.dataset.cpStatSuffix;
    var decimals = parseInt(el.dataset.cpStatDecimals, 10);
    var original = el.dataset.cpStatOriginal;
    var duration = 1200;
    var start    = null;

    function easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    function frame(ts) {
      if (!start) start = ts;
      var elapsed  = ts - start;
      var progress = Math.min(elapsed / duration, 1);
      var eased    = easeOutCubic(progress);
      var current  = target * eased;

      el.textContent = current.toFixed(decimals) + suffix;

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        /* Restore exact original to avoid float drift */
        el.textContent = original;
      }
    }

    el.textContent = (0).toFixed(decimals) + suffix;
    requestAnimationFrame(frame);
  }

  /* ===========================================================
     (F1) MAGNETIC CTAs
     =========================================================== */
  function initMagneticCTAs() {
    if (!fine || reduce) return;

    var magnetSelectors = [
      '.cp-hero__cta',
      '.cp-sf__cta',
      '.cp-pf__cta',
      '.cp-pscroll__cta',
      '.cp-ef__cta'
    ];

    magnetSelectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (btn) {
        btn.addEventListener('pointermove', function (e) {
          var rect    = btn.getBoundingClientRect();
          var halfW   = rect.width  / 2;
          var halfH   = rect.height / 2;
          var offsetX = e.clientX - (rect.left + halfW);
          var offsetY = e.clientY - (rect.top  + halfH);
          var dx = (offsetX / halfW) * 8 * 0.35;
          var dy = (offsetY / halfH) * 8 * 0.35;
          btn.style.transition = 'transform 200ms ease';
          btn.style.transform  = 'translate(' + dx + 'px, ' + dy + 'px)';
        });

        btn.addEventListener('pointerleave', function () {
          btn.style.transform = '';
        });
      });
    });
  }

  /* ===========================================================
     (F2) CARD SPOTLIGHT — Featured categories
     =========================================================== */
  function initCardSpotlight() {
    if (!fine || reduce) return;

    document.querySelectorAll('.cp-cats__card').forEach(function (card) {
      card.addEventListener('pointermove', function (e) {
        var rect = card.getBoundingClientRect();
        var xPct = ((e.clientX - rect.left) / rect.width  * 100).toFixed(1) + '%';
        var yPct = ((e.clientY - rect.top)  / rect.height * 100).toFixed(1) + '%';
        card.style.setProperty('--cp-spot-x', xPct);
        card.style.setProperty('--cp-spot-y', yPct);
      });
    });
  }

  /* ===========================================================
     Bootstrap on DOMContentLoaded (or immediately if ready)
     =========================================================== */
  function init() {
    initScrollReveal();
    initStatCountUp();
    initMagneticCTAs();
    initCardSpotlight();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
